import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import type { Consignee } from '../lib/database.types'

/**
 * Distinguishes "owner" sessions (this user has their own settings + products)
 * from "consignee-only" sessions (mom — bound to someone else's data via a
 * consignees row, but has no owned data of her own).
 *
 * Returns:
 *   loading: true   — auth or detection still resolving
 *   isMomMode: true — user IS a consignee AND has no owned settings row
 *   consignee       — the matched consignees row when isMomMode === true
 *
 * Owners who are ALSO a consignee somewhere else default to non-MomMode
 * (we treat the owner identity as primary). They can be served the owner
 * UI; we can build a switcher later if it ever matters.
 */
export interface MomModeState {
  loading: boolean
  isMomMode: boolean
  consignee: Consignee | null
}

export function useIsMomMode(): MomModeState {
  const { user, loading: authLoading } = useAuth()

  // Is this user a consignee anywhere? (Reads via RLS — mom only sees her own)
  const consigneeQuery = useQuery({
    queryKey: ['mom_mode', 'consignee', user?.id],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async (): Promise<Consignee | null> => {
      const { data, error } = await supabase
        .from('consignees')
        .select('*')
        .eq('consignee_user_id', user!.id)
        .is('revoked_at', null)
        .maybeSingle()
      if (error) throw error
      return data ?? null
    },
  })

  // Does this user have a settings row of their own? Existence = owner.
  // RLS prevents mom from seeing the owner's settings row, so even though
  // a row exists in the DB, this query returns null for mom.
  const settingsQuery = useQuery({
    queryKey: ['mom_mode', 'own_settings', user?.id],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase
        .from('settings')
        .select('id')
        .eq('user_id', user!.id)
        .maybeSingle()
      if (error) throw error
      return !!data
    },
  })

  const loading = authLoading || consigneeQuery.isLoading || settingsQuery.isLoading
  const consignee = consigneeQuery.data ?? null
  const ownsSettings = settingsQuery.data === true

  const isMomMode = !!consignee && !ownsSettings

  return { loading, isMomMode, consignee }
}
